"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable } from "../../dataTables/data-table"
import { deteleCustomer, fetchAssets, fetchCustomers } from "@/apiCalls/apiCalls";
import { Button } from "@/components/ui/button";
import { Customers, customersColumns } from "@/pages/customers/CustomersColumns";
import Spinner from "../spinnner/Spinner";
import CreateCustomerDrawer from "./CreateCustomerDrawer";
import UpdateCustomerDrawer from "./UpdateCustomerDrawer";
import { errorNotification, successNotification } from "@/utils/Notification";
import { jwtDecode } from "jwt-decode"
import useAuth from "@/hooks/useAuth";
import { CustomersDataTables } from "./CustomersDataTables";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "@/context/UseAxiosPrivate";


export default function CustomersFn() {
    const [data, setData] = useState<Customers[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [showCreateCustomerDrawer, setShowCreateCustomerDrawer] = useState(false)
    const [showUpdateCustomerDrawer, setShowUpdateCustomerDrawer] = useState(false)
    const [customerData, setCustomerData] = useState<Customers | undefined>(undefined);
    const navigate = useNavigate();
    const location = useLocation();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth()
    let token: any = auth.accessToken
    const decoded: any = jwtDecode(token);
    let roles: string[] = decoded.UserInfo.roles



    const getCustomers = () => {
        setLoading(true)
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.get('/api/v1/customers', {
            signal: controller.signal
        }).then((response: any) => response.data.data)
            .then((data: Customers[]) => {
                if (isMounted) {
                    setData(data)
                }
            }).catch((err: any) => {
                const errors = err?.response?.data?.errors;
                if (errors && errors?.[0]?.message === "token as expired") {
                    let errror = errors?.[0]?.message
                    errorNotification(
                        "There was an issue",
                        `${errror}`,
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                } else if (errors && Array.isArray(errors) && errors.length > 0) {
                    errors.forEach((error: any) => {
                        const errorMessage = error.message || "An unknown error occurred";
                        errorNotification(
                            "There was an issue",
                            `${errorMessage}`,
                            'topRight'
                        );
                    });
                } else {
                    errorNotification(
                        "There was an issue",
                        "No server reponse",
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                }
            }).finally(() => {
                setLoading(false)
            })
        return () => {
            isMounted = false
            controller.abort()
        };
    };

    useEffect(() => {
        getCustomers()
    }, [])

    
    const deleteCustomerById = (customerId: any) => {
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.delete(`/api/v1/customers/${customerId}`, {
            signal: controller.signal
        }).then((response: any) => response.data.data)
            .then((data: any) => {
                if (isMounted) {
                    successNotification(
                        "Customer successfully deleted",
                        `Customer with id ${customerId} was deleted`,
                        'topRight'
                    );

                }
                getCustomers()
            }).catch((err: any) => {
                const errors = err?.response?.data?.errors;
                if (errors && errors?.[0]?.message === "token as expired") {
                    let errror = errors?.[0]?.message
                    errorNotification(
                        "There was an issue",
                        `${errror}`,
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                } else if (errors && Array.isArray(errors) && errors.length > 0) {
                    errors.forEach((error: any) => {
                        const errorMessage = error.message || "An unknown error occurred";
                        errorNotification(
                            "There was an issue",
                            `${errorMessage}`,
                            'topRight'
                        );
                    });
                } else {
                    errorNotification(
                        "There was an issue",
                        "No server reponse",
                        'topRight'
                    );
                    navigate('/', { state: { from: location }, replace: true });
                }
            })
        return () => {
            isMounted = false;
            controller.abort();
        };
    };


    const editProductFunctionCall = (customers: Customers) => {
        setCustomerData(customers)
        setShowUpdateCustomerDrawer(!showUpdateCustomerDrawer)
    }
    const deleteProductFunctionCall = (customers: Customers) => {
        deleteCustomerById(customers.id)
    }

    const onDelete = useCallback(
        (customers: Customers) => deleteProductFunctionCall(customers),
        []
    )

    const onEdit = useCallback(
        (customers: Customers) => editProductFunctionCall(customers),
        []
    )



    const productsColumnsData = useMemo(() => customersColumns({ onEdit, onDelete, roles }), [])

    if (loading) {
        return <Spinner />
    }

    return (
        <main className='mx-3 mt-16 sm:ml-[300px] sm:mt-3'>

            {roles.includes('customer-create') && (
                <Button
                    variant='default'
                    className="flex items-center justify-end"
                    onClick={() => setShowCreateCustomerDrawer(!showCreateCustomerDrawer)}
                >
                    Create Customer
                </Button>
            )}

            <CustomersDataTables columns={productsColumnsData} data={data} filterNameColumn="uuid" />

            {roles.includes('customer-create') && (
                <CreateCustomerDrawer
                    showCreateCustomerDrawer={showCreateCustomerDrawer}
                    setShowCreateCustomerDrawer={setShowCreateCustomerDrawer}
                    getCustomers={getCustomers}
                />
            )}

            {customerData && roles.includes('customer-edit') && (
                <UpdateCustomerDrawer
                    showUpdateCustomerDrawer={showUpdateCustomerDrawer}
                    setShowUpdateCustomerDrawer={setShowUpdateCustomerDrawer}
                    customers={customerData}
                    getCustomers={getCustomers}
                    setCustomerData={setCustomerData}
                />
            )}
        </main>
    )
}